import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Alert from '../../utils/Alert';
import Button from '../../utils/Button';
import { SelectInput, TextInput } from '../../utils/Form';
import {
  formatCurrency,
  getCanadaProvinces, getCountryList, getUnitedStates,
} from '../../utils/utilities';
import {
  GUEST_LIST_INVITATION_GUEST_CHOICE_DELIVERY_OPTION_ID,
  GUEST_LIST_INVITATION_SHIPPING_AND_MOBILE_DELIVERY_OPTION_ID,
  GUEST_LIST_INVITATION_WILL_CALL_ONLY_DELIVERY_OPTION_ID,
  DELIVERY_METHOD_TYPE_MOBILE_TICKETING_ID,
  DELIVERY_METHOD_TYPE_WILL_CALL_ID,
  DELIVERY_METHOD_TYPE_SHIPPABLE_DOMESTIC_ID, DELIVERY_METHOD_TYPE_SHIPPABLE_INTERNATIONAL_ID,
} from '../../utils/constants';
import { isPOBox, validateZip } from '../../utils/address.util';
import { COUNTRY_ID } from '../../utils/country.constant';

const OptionContainerStyled = styled.div`
  padding: 0 0 0 0;
  margin: 0 0 1rem 0;
`;
const OptionLabelStyled = styled.label`
  font-size: 1.6rem;
  margin: 0 0 0 0;
  padding: 0 0 0 1rem;
`;
const RadioInputStyled = styled.input`
  width: 16px;
  height: 16px;
  opacity: 1;
  color: ${(props) => props.theme.PRIMARY_LINK_COLOR};
  accent-color: ${(props) => props.theme.PRIMARY_LINK_COLOR};
`;
const WillCallNameInputContainerStyled = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
`;

/**
 * Enum-like constant representing delivery method options.
 *
 * @readonly
 * @enum {number}
 *
 * @property {number} WILL_CALL - Represents will-call delivery method.
 * @property {number} SHIPPING - Represents physical shipping delivery method.
 * @property {number} MOBILE_TICKETING - Represents digital/mobile ticketing method.
 */
const DELIVERY_METHOD_OPTION = {
  WILL_CALL: 1,
  SHIPPING: 2,
  MOBILE_TICKETING: 3,
};

const blankAddress = {
  AddressID: '',
  First: '',
  Last: '',
  AddressLineOne: '',
  AddressLineTwo: '',
  City: '',
  State: '',
  Zip: '',
  CountryID: '',
  Phone: '',
  Note: '',
};

const getMobileDeliveryMethod = (outlet) => {
  const { OutletDeliveryMethods } = outlet;
  if (Array.isArray(OutletDeliveryMethods)) {
    return OutletDeliveryMethods.find((x) => x.DeliveryMethodTypeID === DELIVERY_METHOD_TYPE_MOBILE_TICKETING_ID);
  }
  return null;
};

const isMobileDeliveryEnabledForCheckout = (params) => {
  const { cart, outletEvents, mobileDeliveryMethod } = params;
  // RLMTS 3901 / 3889 - Enable Mobile Delivery Rules:
  // 1. Outlet must have an active delivery method of 'Mobile Delivery' type;
  // 2. User's request(cart) must have at least one event that belongs to a series with 'EnableMobileDelivery' flag.
  let isEnabled = false;
  if (mobileDeliveryMethod?.Active && cart && Array.isArray(cart.priceLevels) && Array.isArray(outletEvents)) {
    cart.priceLevels.forEach((priceLevelInCart) => {
      outletEvents.find((oe) => {
        const { OutletEventItems, OutletEventGroup } = oe;
        if (OutletEventGroup && Array.isArray(OutletEventItems)) {
          const { EnableMobileDelivery } = OutletEventGroup;
          OutletEventItems.forEach((oei) => {
            if (Array.isArray(oei.PriceLevels)) {
              const priceLevelMatch = oei.PriceLevels.find((pl) => pl.PriceLevelID === priceLevelInCart.PriceLevelID);
              if (priceLevelMatch && EnableMobileDelivery) {
                isEnabled = true;
              }
            }
          });
        }
      });
    });
  }
  return isEnabled;
};

const getMobileDeliveryNotes = (params) => {
  // RLMTS-3976 - Display mobile delivery shipping note rules:
  // 1. Display shipping note found on the series
  // 2. If rule above is null then use the default note on the delivery method object.
  const { cart, outletEvents, mobileDeliveryMethod } = params;
  let mobileDeliveryShippingNote = mobileDeliveryMethod?.OverrideNotes || '';
  if (cart && Array.isArray(cart.priceLevels) && Array.isArray(outletEvents)) {
    cart.priceLevels.forEach((priceLevelInCart) => {
      outletEvents.find((oe) => {
        const { OutletEventItems, OutletEventGroup } = oe;
        if (OutletEventGroup && Array.isArray(OutletEventItems)) {
          const { EnableMobileDelivery, ShippingNote } = OutletEventGroup;
          OutletEventItems.forEach((oei) => {
            if (Array.isArray(oei.PriceLevels)) {
              const priceLevelMatch = oei.PriceLevels.find((pl) => pl.PriceLevelID === priceLevelInCart.PriceLevelID);
              if (priceLevelMatch && EnableMobileDelivery && ShippingNote) {
                mobileDeliveryShippingNote = ShippingNote;
              }
            }
          });
        }
      });
    });
  }
  return mobileDeliveryShippingNote;
};

const getShippableDeliveryMethods = (deliveryMethodList = []) => {
  let filteredDeliveryMethods = [];
  if (Array.isArray(deliveryMethodList)) {
    filteredDeliveryMethods = deliveryMethodList.filter(
      (x) => x.DeliveryMethodTypeID === DELIVERY_METHOD_TYPE_SHIPPABLE_DOMESTIC_ID || x.DeliveryMethodTypeID ===
        DELIVERY_METHOD_TYPE_SHIPPABLE_INTERNATIONAL_ID,
    );
  }
  return filteredDeliveryMethods;
};

/**
 * GuestListCheckoutDeliveryInfo is a functional React component used in the Guest List checkout flow.
 * It presents delivery method options (Will Call, Shipping, Mobile Ticketing) and renders the corresponding form UI.
 * It validates form inputs and emits a structured payload upon successful submission.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.cart - The cart state with delivery and guest details.
 * @param {string} [props.error] - Optional error message to display.
 * @param {Object} props.outlet - Outlet data including delivery methods.
 * @param {boolean} props.loading - Whether the form is submitting or loading.
 * @param {Function} props.onSubmit - Callback to be triggered with payload on valid submission.
 * @param {Array} props.outletEvents - List of outlet events for delivery logic.
 * @param {Object} props.defaultAddress - The default shipping address to pre-fill.
 * @param {Array} props.userAddressList - List of saved addresses for selection.
 * @param {Array} props.deliveryMethodList - Full list of delivery methods available.
 * @param {Object} props.guestListInvitation - Guest list invitation metadata including allowed delivery methods.
 *
 * @return {JSX.Element} Rendered component containing the delivery form logic.
 *
 * @example
 * <GuestListCheckoutDeliveryInfo
 *   cart={cart}
 *   error={error}
 *   outlet={outlet}
 *   loading={isLoading}
 *   onSubmit={handleDeliverySubmit}
 *   outletEvents={events}
 *   defaultAddress={defaultAddress}
 *   userAddressList={userAddresses}
 *   deliveryMethodList={deliveryMethods}
 *   guestListInvitation={invitation}
 * />
 */
export const GuestListCheckoutDeliveryInfo = (props = {}) => {
  const {
    cart,
    error,
    outlet,
    loading,
    onSubmit,
    outletEvents,
    defaultAddress,
    userAddressList,
    deliveryMethodList,
    guestListInvitation,
  } = props;
  console.log('GuestListCheckoutDeliveryInfo props', props);

  const [address, setAddress] = useState(defaultAddress || blankAddress);
  console.log('GuestListCheckoutDeliveryInfo address', address);
  const [formErrors, setFormErrors] = useState({});
  const [deliveryOption, setDeliveryOption] = useState(cart && cart.deliveryOption || null);
  const [GuestFirstName, setWillCallPickupFirstName] = useState(cart && cart.GuestFirstName || '');
  const [GuestLastName, setWillCallPickupLastName] = useState(cart && cart.GuestLastName || '');
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState(cart?.deliveryMethodUUID || '');

  console.log(`what is selectedDeliveryMethod`, selectedDeliveryMethod);

  /**
   * Validates the Will Call pickup form by checking for required guest name fields.
   *
   * @function
   * @return {Object} An object containing validation errors keyed by field name.
   *                   Returns an empty object if all fields are valid.
   *
   * @example
   * const errors = validateWillCallForm();
   * if (errors.GuestFirstName) {
   *   // Handle missing first name
   * }
   */
  const validateWillCallForm = () => {
    const formErrors = {};
    const isEmpty = (value) => String(value).trim() === '';

    if (isEmpty(GuestFirstName)) formErrors.GuestFirstName = 'First Name Required';
    if (isEmpty(GuestLastName)) formErrors.GuestLastName = 'Last Name Required';

    return formErrors;
  };

  /**
   * Validates a shipping address object by checking required fields and postal code format.
   *
   * This function ensures the address contains all necessary fields such as name, street,
   * city, state, and zip. It also checks that the zip/postal code is valid for the given country.
   * Additionally, it rejects PO Boxes for shipping addresses.
   *
   * This is the same as the validateAddress function from address.util.js minus the phone number validation,
   * since phone number field is on a different form(contact section form in step 1 of guest list checkout)
   *
   * @param {Object} address - The shipping address object to validate.
   * @param {string} address.First - The recipient's first name.
   * @param {string} address.Last - The recipient's last name.
   * @param {string|number} address.CountryID - The country ID (used to determine zip format).
   * @param {string} address.AddressLineOne - The street address.
   * @param {string} address.City - The city name.
   * @param {string} address.State - The state or province.
   * @param {string} address.Zip - The zip or postal code.
   *
   * @return {Object} An object containing field-specific error messages (if any).
   *
   * @example
   * const errors = validateShippingAddress({
   *   First: 'John',
   *   Last: '',
   *   CountryID: '1',
   *   AddressLineOne: '123 Main St',
   *   City: 'New York',
   *   State: 'NY',
   *   Zip: '10001'
   * });
   * // errors = { Last: 'Last Name Required' }
   */
  const validateShippingAddress = (address) => {
    const formErrors = {};
    const isEmpty = (value) => String(value).trim() === '';

    if (isEmpty(address.First)) formErrors.First = 'First Name Required';
    if (isEmpty(address.Last)) formErrors.Last = 'Last Name Required';
    if (isEmpty(address.CountryID)) formErrors.CountryID = 'Country Required';

    if (isEmpty(address.AddressLineOne)) {
      formErrors.AddressLineOne = 'Street Address Required';
    } else if (isPOBox(address.AddressLineOne)) {
      formErrors.AddressLineOne = 'Please enter a valid street name for shipping address';
    }

    if (isEmpty(address.City)) formErrors.City = 'City Required';
    if (isEmpty(address.State)) formErrors.State = 'State Required';

    if (isEmpty(address.Zip)) {
      formErrors.Zip = 'Zip/Postal Code Required';
    } else {
      const zipError = validateZip(address.Zip, Number(address.CountryID));
      if (zipError) formErrors.Zip = zipError;
    }

    return formErrors;
  };

  const handleSubmitAllFormData = () => {
    let payload;
    let errors = {};

    if (deliveryOption === DELIVERY_METHOD_OPTION.WILL_CALL) {
      errors = validateWillCallForm();
    }

    if (deliveryOption === DELIVERY_METHOD_OPTION.SHIPPING) {
      errors = validateShippingAddress(address);

      if (!selectedDeliveryMethod) {
        errors.selectedDeliveryMethod = 'Delivery Method Required';
      }
    }

    setFormErrors(errors);

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return false;
    } else {
      const outletDeliveryMethodList = outlet && Array.isArray(outlet.OutletDeliveryMethods)
        ? [...outlet.OutletDeliveryMethods]
        : [];

      if (deliveryOption === DELIVERY_METHOD_OPTION.WILL_CALL) {
        const deliveryMethod = outletDeliveryMethodList
          .find((dm) => dm?.DeliveryMethodType?.ConstantValue === 'DELIVERY_METHOD_TYPE_WILL_CALL');

        if (deliveryMethod) {
          payload = {
            deliveryMethod,
            shippingAddress: null,
            GuestFirstName: GuestFirstName,
            GuestLastName: GuestLastName,
            deliveryMethodUUID: deliveryMethod && deliveryMethod.OutletDeliveryMethodUUID,
            deliveryOption,
          };
        } else {
          setFormErrors({ DeliveryMethod: 'No will call delivery method found' });
          return false;
        }
      }

      if (deliveryOption === DELIVERY_METHOD_OPTION.SHIPPING) {
        const deliveryMethod = outletDeliveryMethodList
          .find((dm) => dm.OutletDeliveryMethodUUID === selectedDeliveryMethod);

        payload = {
          deliveryMethod,
          shippingAddress: address,
          deliveryMethodUUID: deliveryMethod && deliveryMethod.OutletDeliveryMethodUUID,
          deliveryOption,
        };
      }

      if (deliveryOption === DELIVERY_METHOD_OPTION.MOBILE_TICKETING) {
        const deliveryMethod = outletDeliveryMethodList
          .find((dm) => dm?.DeliveryMethodType?.ConstantValue === 'DELIVERY_METHOD_TYPE_MOBILE_TICKETING');

        if (deliveryMethod) {
          payload = {
            deliveryMethod,
            shippingAddress: null,
            deliveryMethodUUID: deliveryMethod.OutletDeliveryMethodUUID,
            deliveryOption,
          };
        } else {
          setFormErrors({ DeliveryMethod: 'No mobile ticket delivery method found' });
          return false;
        }
      }

      if (onSubmit && payload) {
        onSubmit(payload);
      }
    }
  };

  const handleSelectAddress = (e) => {
    const AddressID = e.target.value;
    if (!AddressID) {
      setAddress(blankAddress);
    } else {
      if (Array.isArray(userAddressList)) {
        const newAddress = userAddressList.find((a) => Number(a.AddressID) === Number(AddressID));
        setAddress(newAddress);
      } else {
        setAddress(blankAddress);
      }
    }
  };

  const handleAddressInput = (event) => {
    const n = event.target.name;
    const value = event.target.value;
    const newAddress = Object.assign({}, address);
    newAddress[n] = value;
    if (n === 'CountryID') {
      newAddress.State = '';
      setSelectedDeliveryMethod('');
    }
    setAddress(newAddress);
  };

  const countryOptionList = [
    { value: '', label: '-- Select Country (required) --' },
    ...getCountryList().map((c) => ({
      value: c.id,
      label: c.name,
    })),
  ];

  const countryID = address?.CountryID ? parseInt(address.CountryID, 10) : COUNTRY_ID.UNITED_STATES;

  const statesList = countryID === COUNTRY_ID.CANADA ? getCanadaProvinces() : getUnitedStates();

  const stateOptionList = [
    { value: '', label: '-- Select State (required) --' },
    ...statesList.map((c) => ({
      value: c.abbreviation,
      label: c.name,
    })),
  ];

  const userAddressOptions = [
    { value: '', label: '-- Use a new address --' },
    ...(Array.isArray(userAddressList)
      ? userAddressList.map((x) => ({
        value: x.AddressID,
        label: `${x.AddressLineOne}, ${x.City} ${x.State}, ${x.Zip}`,
      }))
      : []),
  ];

  const deliveryMethodOptionList = [{ value: '', label: '-- Shipping Method (required) --' }];

  const shippableDeliveryMethods = getShippableDeliveryMethods(deliveryMethodList);

  let filteredDeliveryMethods = Array.isArray(deliveryMethodList) ? [...deliveryMethodList] : [];

  if (address && Number(address.CountryID) === COUNTRY_ID.UNITED_STATES) {
    filteredDeliveryMethods = shippableDeliveryMethods.filter((x) => x.DeliveryMethodTypeID === DELIVERY_METHOD_TYPE_SHIPPABLE_DOMESTIC_ID);
  } else {
    filteredDeliveryMethods = shippableDeliveryMethods.filter((x) => x.DeliveryMethodTypeID === DELIVERY_METHOD_TYPE_SHIPPABLE_INTERNATIONAL_ID,
    );
  }

  let deliveryOptions = []; // options are added dynamically based on guest list invitation rules below
  let mobileDeliveryDescription = '';

  if (guestListInvitation) {
    const { OutletDeliveryMethodID } = guestListInvitation;
    const mobileDeliveryMethod = getMobileDeliveryMethod(outlet);
    const mobileDeliveryIsEnabled = isMobileDeliveryEnabledForCheckout({ mobileDeliveryMethod, cart, outletEvents });
    mobileDeliveryDescription = getMobileDeliveryNotes({ cart, outletEvents, mobileDeliveryMethod });

    if (Number(OutletDeliveryMethodID) === GUEST_LIST_INVITATION_SHIPPING_AND_MOBILE_DELIVERY_OPTION_ID) {
      if (shippableDeliveryMethods.length) {
        deliveryOptions = [{ id: DELIVERY_METHOD_OPTION.SHIPPING, name: 'Shipping' }];
      }
      if (mobileDeliveryIsEnabled) {
        deliveryOptions.push({ id: DELIVERY_METHOD_OPTION.MOBILE_TICKETING, name: 'Mobile Delivery' });
      }
      filteredDeliveryMethods = filteredDeliveryMethods.filter(
        (dm) => dm.DeliveryMethodTypeID !== DELIVERY_METHOD_TYPE_WILL_CALL_ID,
      );
    }
    if (Number(OutletDeliveryMethodID) === GUEST_LIST_INVITATION_WILL_CALL_ONLY_DELIVERY_OPTION_ID) {
      deliveryOptions = [{ id: DELIVERY_METHOD_OPTION.WILL_CALL, name: 'Will Call' }];
      filteredDeliveryMethods = filteredDeliveryMethods.filter(
        (dm) => dm.DeliveryMethodTypeID === DELIVERY_METHOD_TYPE_WILL_CALL_ID,
      );
    }

    if (Number(OutletDeliveryMethodID) === GUEST_LIST_INVITATION_GUEST_CHOICE_DELIVERY_OPTION_ID) {
      deliveryOptions = [{ id: DELIVERY_METHOD_OPTION.WILL_CALL, name: 'Will Call' }];
      if (shippableDeliveryMethods.length) {
        deliveryOptions.push({ id: DELIVERY_METHOD_OPTION.SHIPPING, name: 'Shipping' });
      }
      if (mobileDeliveryIsEnabled) {
        deliveryOptions.push({ id: DELIVERY_METHOD_OPTION.MOBILE_TICKETING, name: 'Mobile Delivery' });
      }
    }
  }

  if (deliveryOption === DELIVERY_METHOD_OPTION.SHIPPING) {
    if (!address || !address.CountryID) {
      deliveryMethodOptionList.push({ value: '-1', label: 'Please select a country above', disabled: true });
    } else {
      filteredDeliveryMethods = filteredDeliveryMethods
        .filter((dm) => dm.DeliveryMethodTypeID !== DELIVERY_METHOD_TYPE_WILL_CALL_ID);
      filteredDeliveryMethods.forEach((dm) => {
        const value = dm.OutletDeliveryMethodUUID;
        let label = dm.OverrideName + ' - ';
        if (!dm.OverridePrice || Number(dm.OverridePrice) <= 0) {
          label += 'Free';
        } else {
          label += formatCurrency(dm.OverridePrice);
        }

        deliveryMethodOptionList.push({ value, label });
      });
    }
  }

  return (
    <div id="guest-list-checkout-delivery-info-container">
      {deliveryOptions.map((option, idx) => {
        return (
          <OptionContainerStyled key={idx}>
            <RadioInputStyled
              data-testid={`${option.name.replace(/\s+/g, '-').toLowerCase()}-radio-input`}
              type="radio"
              id={option.id}
              value={option.id}
              name={option.name}
              checked={deliveryOption === option.id}
              onChange={(e) => {
                setFormErrors({});
                setDeliveryOption(Number(e.target.value));
              }}
            />
            <OptionLabelStyled htmlFor={option.id}>
              {option.name}
            </OptionLabelStyled>
          </OptionContainerStyled>
        );
      })}

      {deliveryOption === DELIVERY_METHOD_OPTION.WILL_CALL && (
        <WillCallNameInputContainerStyled>
          <TextInput
            required
            fullWidth
            id="guest-first-name-input"
            name="GuestFirstName"
            label="Guest First Name"
            value={GuestFirstName}
            onChange={(e) => setWillCallPickupFirstName(e.target.value)}
            error={formErrors['GuestFirstName']}
          />
          <TextInput
            required
            fullWidth
            id="guest-last-name-input"
            name="GuestLastName"
            label="Guest Last Name"
            value={GuestLastName}
            onChange={(e) => setWillCallPickupLastName(e.target.value)}
            error={formErrors['GuestLastName']}
          />
        </WillCallNameInputContainerStyled>
      )}

      {/* Note: We do not use the reusable <AddressForm /> component here because guest list checkout flow
          behaves differently than the rest of the checkout flows. In guest list flow, the phone number field is
          broken out into a separate Contact section. */}
      {deliveryOption === DELIVERY_METHOD_OPTION.SHIPPING && (
        <>
          <div style={{ margin: '0 0 2rem 0' }}>
            Please enter a shipping address for this request. This
            information will be used in the approval process and to ship
            tickets if you selected a shipping method.
          </div>
          <SelectInput
            id="select-existing-address-input"
            name="address"
            label="Saved Addresses"
            value={address && address.AddressID}
            onChange={handleSelectAddress}
            style={{ width: '100%' }}
            optionList={userAddressOptions}
          />
          <TextInput
            id="first-name-input"
            name="First"
            label="First Name"
            error={formErrors.First}
            fullWidth
            value={address.First}
            onChange={handleAddressInput}
            required
          />
          <TextInput
            id="last-name-input"
            name="Last"
            label="Last Name"
            error={formErrors.Last}
            fullWidth
            value={address.Last}
            onChange={handleAddressInput}
            required
          />
          <TextInput
            id="company-name-input"
            name="CompanyName"
            label="Company"
            error={formErrors.CompanyName}
            fullWidth
            value={address.CompanyName}
            onChange={handleAddressInput}
          />
          <SelectInput
            id="country-select"
            name="CountryID"
            label="Country"
            value={address.CountryID}
            error={formErrors.CountryID}
            onChange={handleAddressInput}
            optionList={countryOptionList}
            required
          />
          <TextInput
            id="address-line-one-input"
            name="AddressLineOne"
            label="Street Address"
            error={formErrors.AddressLineOne}
            fullWidth
            value={address.AddressLineOne}
            onChange={handleAddressInput}
            required
          />
          <TextInput
            id="address-line-two-input"
            name="AddressLineTwo"
            label="Apt / Suite / Other"
            error={formErrors.AddressLineTwo}
            fullWidth
            value={address.AddressLineTwo}
            onChange={handleAddressInput}
          />
          <TextInput
            id="city-input"
            name="City"
            label="City"
            error={formErrors.City}
            fullWidth
            value={address.City}
            onChange={handleAddressInput}
            required
          />
          <SelectInput
            id="state-select"
            name="State"
            label="State"
            value={address.State}
            onChange={handleAddressInput}
            error={formErrors.State}
            optionList={stateOptionList}
            required
          />
          <TextInput
            id="zip-input"
            name="Zip"
            label="Zip/Postal Code"
            error={formErrors.Zip}
            fullWidth
            value={address.Zip}
            onChange={handleAddressInput}
            required
          />
          <SelectInput
            id="deliver-method-input"
            name="deliveryMethod"
            label="Delivery Method"
            style={{ width: '100%' }}
            value={selectedDeliveryMethod}
            optionList={deliveryMethodOptionList}
            error={formErrors.selectedDeliveryMethod}
            onChange={(e) => setSelectedDeliveryMethod(e.target.value)}
            required
          />
        </>
      )}

      {deliveryOption === DELIVERY_METHOD_OPTION.MOBILE_TICKETING && (
        <div
          dangerouslySetInnerHTML={{
            __html: mobileDeliveryDescription,
          }}
        />
      )}

      {error && <Alert style={{ margin: '2rem 0' }}>{error}</Alert>}

      <div style={{ margin: '3rem 0 ' }}>
        {deliveryOption && (<Button
          fullWidth
          loading={loading}
          id="add-delivery-method-btn"
          onClick={handleSubmitAllFormData}
        >
          Continue
        </Button>)}
      </div>

    </div>
  );
};

GuestListCheckoutDeliveryInfo.defaultProps = {
  cart: null,
  error: null,
  loading: false,
  onSubmit: null,
  defaultAddress: null,
  userAddressList: [],
  deliveryMethodList: [],
  guestListInvitation: null,
};

GuestListCheckoutDeliveryInfo.propTypes = {
  cart: PropTypes.object,
  error: PropTypes.any,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  defaultAddress: PropTypes.any,
  userAddressList: PropTypes.array,
  deliveryMethodList: PropTypes.array,
  guestListInvitation: PropTypes.object,
  defaultDeliveryMethod: PropTypes.object,

};

export default GuestListCheckoutDeliveryInfo;
